import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Dropdown, Input, Modal, Space, Table, Tag, Typography } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  EyeOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import paths from "routes/paths";
import { useAuth } from "contexts/AuthContext";
import useBusinessEntities from "hooks/olreadia/useBusinessEntities";
import useBusinessLocationTypes from "hooks/olreadia/useBusinessLocationTypes";
import BusinessEntityDrawer from "./BusinessEntityDrawer";
import { formatEnumLabel } from "../constants";
 

const statusColor = (status) => {
  if (!status) return "default";
  if (status === "ACTIVE" || status === "APPROVED") return "green";
  if (status === "DRAFT" || status === "PENDING_REVIEW") return "gold";
  if (status === "SUSPENDED") return "red";
  if (status === "DELETED") return "default";
  return "blue";
};

export default function BusinessEntitiesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const actorId = user?.id || "";

  const { entities, loading, error, page, list, create, update, remove } =
    useBusinessEntities();
  const { locationTypes, locationTypesLoading, fetchLocationTypes } =
    useBusinessLocationTypes();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [query, setQuery] = useState("");
 

  const fetchPage = async (pag = pagination) => {
    await list({ page: pag.current - 1, size: pag.pageSize });
  };

  useEffect(() => {
    fetchPage();
    fetchLocationTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = () => fetchPage();

  const handleCreate = () => {
    setEditing(null);
    setDrawerOpen(true);
  };

  const handleEdit = (record) => {
    setEditing(record);
    setDrawerOpen(true);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: "Remove business entity?",
      content: "This will deactivate/remove the business entity.",
      okText: "Remove",
      okButtonProps: { danger: true },
      onOk: async () => {
        await remove(record.id, actorId);
        await fetchPage();
      },
    });
  };

  const handleSubmit = async (payload) => {
    if (editing?.id) {
      await update(editing.id, payload, actorId);
    } else {
      await create(payload, actorId);
    }
    setDrawerOpen(false);
    await fetchPage();
  };

  const filteredEntities = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return entities;
    return (entities || []).filter((e) => {
      const status = e?.status ? String(e.status) : "";
      const name = e?.name ? String(e.name) : "";
      const ein = e?.ein ? String(e.ein) : "";
      return (
        name.toLowerCase().includes(q) ||
        status.toLowerCase().includes(q) ||
        ein.toLowerCase().includes(q)
      );
    });
  }, [entities, query]);

  const resolveEntityId = (record) =>
    record?.id ||
    record?.businessEntityId ||
    record?.entityId ||
    record?.uuid ||
    null;

  const columns = useMemo(
    () => [
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        ellipsis: true,
      },
      {
        title: "Email",
        key: "contact",
        ellipsis: true,
        render: (_, record) => {
          const email = record?.contactInformation?.primaryEmail;
          return (
            <div style={{ lineHeight: 1.2 }}>
              <Typography.Text style={{ display: "block" }} ellipsis>
                {email || "-"}
              </Typography.Text>
            </div>
          );
        },
      },
      {
        title: "Address",
        key: "location",
        ellipsis: true,
        render: (_, record) => {
          const addressLine1 = record?.locationInformation?.addressLine1;
          return (
            <div style={{ lineHeight: 1.2 }}>
              <Typography.Text style={{ display: "block" }} ellipsis>
                {addressLine1 || "-"}
              </Typography.Text>
            </div>
          );
        },
      },
      {
        title: "EIN",
        dataIndex: "ein",
        key: "ein",
        ellipsis: true,
        render: (val) => val || "-",
      },
      {
        title: "Location Type",
        dataIndex: "businessLocationType",
        key: "businessLocationType",
        ellipsis: true,
        render: (val) => formatEnumLabel(val?.code || val) || "-",
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (val) => <Tag color={statusColor(val)}>{formatEnumLabel(val)}</Tag>,
      },
      {
        title: "Actions",
        key: "actions",
        render: (_, record) => (
          <div className="be-actions" onClick={(e) => e.stopPropagation()}>
            <Dropdown
              trigger={["click"]}
              menu={{
                items: [
                  {
                    key: "view",
                    label: "View",
                    icon: <EyeOutlined />,
                    onClick: ({ domEvent }) => {
                      domEvent.stopPropagation();
                      const id = resolveEntityId(record);
                      if (!id) return;
                      navigate(paths.businessEntityDetails(id), {
                        state: { entity: record },
                      });
                    },
                  },
                  {
                    key: "edit",
                    label: "Edit",
                    icon: <EditOutlined />,
                    onClick: ({ domEvent }) => {
                      domEvent.stopPropagation();
                      handleEdit(record);
                    },
                  },
                  {
                    key: "remove",
                    label: "Remove",
                    icon: <DeleteOutlined />,
                    danger: true,
                    onClick: ({ domEvent }) => {
                      domEvent.stopPropagation();
                      handleDelete(record);
                    },
                  },
                ],
              }}
            >
              <Button size="small" type="text" aria-label="Actions">
                <EllipsisOutlined style={{ fontSize: 18 }} />
              </Button>
            </Dropdown>
          </div>
        ),
      },
    ],
    [navigate],
  );

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2 mt-2 gap-3">
        <WidgetSectionTitle
          icon="building"
          title="Business Entities"
          subtitle="Create and manage business entities, their lines, services, and users."
          className="mb-2 mt-2 flex-1"
        />
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            style={{ paddingInline: 14 }}
          >
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            New Entity
          </Button>
        </Space>
      </div>

      {error ? (
        <Alert
          type="error"
          showIcon
          message="Failed to load"
          description={error}
          style={{ marginBottom: 12 }}
          action={
            <Button size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }
        />
      ) : null}

      <Table
        rowKey={(record) => resolveEntityId(record) || record?.name}
        loading={loading}
        columns={columns}
        dataSource={filteredEntities}
        className="custom-table"
        tableLayout="auto"
        scroll={{ x: "max-content" }}
        size="small"
        style={{ borderRadius: 10, overflow: "hidden", marginTop: 8 }}
        title={() => (
          <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
            <Input.Search
              allowClear
              placeholder="Search by name, status, EIN"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{ width: 420 }}
            />
          </div>
        )}
       
        pagination={{
          ...pagination,
          total: page.totalElements,
          size: "small",
          showSizeChanger: true,
          pageSizeOptions: ["20", "50", "100"],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
        }}
        onChange={(pag) => {
          const next = { current: pag.current, pageSize: pag.pageSize };
          setPagination(next);
          list({ page: next.current - 1, size: next.pageSize });
        }}
        onRow={(record) => ({
          onClick: (e) => {
            if (e?.target?.closest?.(".be-actions")) return;
            if (e?.target?.closest?.(".ant-table-row-expand-icon")) return;
            if (e?.target?.closest?.(".ant-table-row-expand-icon-cell")) return;
            const id = resolveEntityId(record);
            if (!id) return;
            navigate(paths.businessEntityDetails(id), { state: { entity: record } });
          },
        })}
        expandable={undefined}
        locale={{
          emptyText: "No business entities found for the selected filters.",
        }}
      />

      <BusinessEntityDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={handleSubmit}
        loading={loading}
        initialValues={editing}
        locationTypes={locationTypes}
        locationTypesLoading={locationTypesLoading}
      />
    </div>
  );
}
